// SuppressAlertTitleModal — quick-suppress modal opened from the Detection Alerts
// Actions column (v1.101.0), for Microsoft Defender `security_alert` pass-through
// alerts. Appends the alert's title to the FIRING rule's own
// `parameters_json.excluded_alert_titles` via the dedicated SuppressAlertTitle endpoint
// (which works on PROD rules — a narrow list mutation, not a full rule edit).
//
// The blast radius = the firing rule's existing scope (these rules are global by
// default), so the modal surfaces that explicitly before the analyst confirms: adding
// a title to a global rule suppresses it for EVERY tenant the rule covers. There is no
// per-tenant copy here by design (see plan / handoff).

import React, { useState } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { suppressAlertTitle } from '../../api/client';

function splitCount(csv) {
  return (csv || '').split(',').map((s) => s.trim()).filter(Boolean).length;
}

export default function SuppressAlertTitleModal({ alert, ruleMeta, onClose }) {
  // The alert's `operation` field IS the Defender $.title (handoff §3).
  const [title, setTitle] = useState((alert.operation || '').trim());
  const [mode, setMode] = useState('exact');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const includeCount = splitCount(ruleMeta?.includedTenantIds);
  const excludeCount = splitCount(ruleMeta?.excludedTenantIds);
  const scopeText =
    includeCount > 0 ? `only ${includeCount} scoped tenant${includeCount === 1 ? '' : 's'}`
      : excludeCount > 0 ? `all tenants except ${excludeCount}`
        : 'ALL tenants';
  const scopeTone = includeCount > 0 ? 'text-sky-300' : 'text-amber-300';

  const trimmedTitle = title.trim();
  const blocker = !alert.ruleId ? 'This alert has no rule id.'
    : !trimmedTitle ? 'Enter the alert title to suppress.'
      : null;

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      await suppressAlertTitle(alert.ruleId, { title: trimmedTitle, mode });
      setApplied(true);
    } catch (err) {
      setError(err.message || 'Failed to suppress alert title');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Suppress this alert title</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : 'Defender pass-through rule'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Alert title suppressed.</p>
                <p className="text-emerald-300/80 mt-1">
                  Alerts titled <span className="font-mono">“{trimmedTitle}”</span> ({mode}) will no longer be raised by this rule.
                  Takes effect ~10 minutes after your last rule edit, once the detection streaming job restarts.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Blast radius */}
              <div className="p-3 bg-gray-800/60 border border-gray-700/60 rounded text-xs text-gray-400 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-500" />
                <span>
                  This adds the title to the firing rule directly — it will be suppressed for{' '}
                  <span className={`font-medium ${scopeTone}`}>{scopeText}</span> this rule applies to (no per-tenant copy).
                  A known-malicious IP still alerts.
                </span>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Alert title</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Password spray attack detected" className="input-field w-full" />
                <p className="text-[11px] text-gray-500 mt-1">Matched case-insensitively after trimming whitespace. No wildcards / regex.</p>
              </div>

              {/* Match mode */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Match</label>
                <div className="space-y-1.5 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="modeSel" checked={mode === 'exact'} onChange={() => setMode('exact')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Exact title
                      <span className="text-[11px] text-gray-500 block">Suppress only this exact title.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="modeSel" checked={mode === 'contains'} onChange={() => setMode('contains')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Contains
                      <span className="text-[11px] text-gray-500 block">Suppress any title that contains this text (broader — use with care).</span>
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Suppress title
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
